import { Group, Stack, ToggleButton } from '@ekhodealer/ekho-common/rev';

export interface ToggleButtonOption<T extends string> {
	value: T;
	label: string | React.ReactNode;
	icon?: React.ReactElement;
}

interface RenderToggleButtonGroupsProps<T extends string> {
	options: ToggleButtonOption<T>[];
	currentValue: T | T[] | null;
	onToggle: (value: T) => (isSelected: boolean) => void;
	groupSize?: number;
}

/**
 * Renders toggle buttons in groups (default: 2 per group)
 * Useful for filter sections that need pairs of toggle buttons
 * Supports both single selection (currentValue: T | null) and multi-selection (currentValue: T[])
 */
export function renderToggleButtonGroups<T extends string>({
	options,
	currentValue,
	onToggle,
	groupSize = 2,
}: RenderToggleButtonGroupsProps<T>) {
	const groups: ToggleButtonOption<T>[][] = [];

	// Split options into groups of specified size
	for (let i = 0; i < options.length; i += groupSize) {
		groups.push(options.slice(i, i + groupSize));
	}

	// Helper to check if a value is selected
	const isSelected = (value: T): boolean => {
		if (Array.isArray(currentValue)) {
			return currentValue.includes(value);
		}
		return currentValue === value;
	};

	return (
		<Stack itemsSpacing={8}>
			{groups.map((group, groupIndex) => (
				<Group key={groupIndex} itemsSpacing={8} itemsAlignX='stretch'>
					{group.map((option) => (
						<ToggleButton
							key={option.value}
							rounding='full'
							size='large'
							variant='outline'
							selectedVariant='filled'
							isSelected={isSelected(option.value)}
							onChange={onToggle(option.value)}
							icon={option.icon}
							iconSide='left'>
							{option.label}
						</ToggleButton>
					))}
				</Group>
			))}
		</Stack>
	);
}
